import React from 'react'
import { MediaBlock } from '@/blocks/MediaBlock/Component'
import { DefaultNodeTypes, SerializedBlockNode } from '@payloadcms/richtext-lexical'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import {
  JSXConvertersFunction,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react'

import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component'

import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  MediaBlock as MediaBlockProps,
} from '@/payload-types'
import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { cn } from '@/utilities/cn'
import { slugifyHeading } from '@/utilities/slugifyHeading'
import { nodeText } from '@/utilities/articles'
import { fixLexicalTree } from '@/utilities/markdownToLexical'
import { refId, sourceId, transformArticleTree } from '@/utilities/articleFaq'

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<CTABlockProps | MediaBlockProps | BannerBlockProps | CodeBlockProps>

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  // Article-only custom nodes created by transformArticleTree()
  faqAccordion: ({ node, nodesToJSX }: any) => (
    <div className="blog-faq" data-testid="article-faq">
      {node.items.map((it: any) => (
        <details key={it.id} id={it.id} className="blog-faq-item">
          <summary className="blog-faq-q">{it.question}</summary>
          <div className="blog-faq-a">{nodesToJSX({ nodes: it.children })}</div>
        </details>
      ))}
    </div>
  ),
  sourceRef: ({ node }: any) => (
    <sup className="blog-ref">
      <a id={refId(node.n, node.k)} href={`#${sourceId(node.n)}`} aria-label={`Quelle ${node.n}`}>
        [{node.label}]
      </a>
    </sup>
  ),
  listitem: (args: any) => {
    const { node, nodesToJSX } = args
    if (!node.sourceNo) return (defaultConverters as any).listitem(args)
    const back = Array.from({ length: node.refCount || 0 }, (_, i) => (
      <a key={i} className="blog-ref-back" href={`#${refId(node.sourceNo, i + 1)}`} aria-label="Zurück zur Textstelle">
        ↩
      </a>
    ))
    return (
      <li id={sourceId(node.sourceNo)} value={node.sourceNo} className="blog-source">
        {nodesToJSX({ nodes: node.children })} {back}
      </li>
    )
  },
  heading: ({ node, nodesToJSX }) => {
    const Tag = node.tag
    const children = nodesToJSX({ nodes: node.children })
    // Anchor ids for the article TOC (h2/h3 only).
    const id = Tag === 'h2' || Tag === 'h3' ? slugifyHeading(nodeText(node)) : undefined
    return <Tag id={id || undefined}>{children}</Tag>
  },
  blocks: {
    banner: ({ node }) => <BannerBlock className="col-start-2 mb-4" {...node.fields} />,
    mediaBlock: ({ node }) => (
      <MediaBlock
        className="col-start-1 col-span-3"
        imgClassName="m-0"
        {...node.fields}
        captionClassName="mx-auto max-w-[48rem]"
        enableGutter={false}
        disableInnerContainer={true}
      />
    ),
    code: ({ node }) => <CodeBlock className="col-start-2" {...node.fields} />,
    cta: ({ node }) => <CallToActionBlock {...node.fields} />,
  },
})

type Props = {
  data: SerializedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  variant?: 'article' | 'product'
} & React.HTMLAttributes<HTMLDivElement>

// Deep clone and sanitize the AST to remove non-breaking spaces (\u00a0)
// This prevents layout blowouts caused by pasted rich text containing &nbsp;
function sanitizeAST(data: SerializedEditorState, article = false, demoteH1 = false): SerializedEditorState {
  if (!data || !data.root) return data

  const root = JSON.parse(JSON.stringify(data.root))
  const replaceNbsp = (node: any) => {
    if (node.type === 'text' && typeof node.text === 'string') {
      node.text = node.text.replace(/\u00a0/g, ' ')
    }
    node.children?.forEach?.(replaceNbsp)
  }
  replaceNbsp(root)
  // Safety net: literal `**bold**` left in stored text becomes real bold/italic
  fixLexicalTree(root)
  if (article) transformArticleTree(root)
  if (demoteH1) {
    // The page already has its own <h1>; headings inside the content start at h2.
    const demote = (node: any) => {
      if (node.type === 'heading' && node.tag === 'h1') node.tag = 'h2'
      node.children?.forEach?.(demote)
    }
    demote(root)
  }

  return { ...data, root }
}

export const RichText: React.FC<Props> = (props) => {
  const { className, enableProse = true, enableGutter = true, data, variant, ...rest } = props
  
  // Sanitize the data before passing it to the renderer
  const sanitizedData = React.useMemo(() => sanitizeAST(data, variant === 'article', variant === 'product'), [data, variant])

  return (
    <RichTextWithoutBlocks
      converters={jsxConverters}
      data={sanitizedData}
      className={cn(
        // Add strong CSS word-break constraints to prevent flex/grid blowouts globally
        'break-words [word-break:break-word] overflow-hidden max-w-full',
        {
          'container ': enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md dark:prose-invert ': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
